import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";

import {
  connectAdminSocket,
  disconnectAdminSocket,
  WEALL_TEAM_EVENTS,
} from "@/libs/socket";
import { setWeAllTeamUnread } from "@/store/features/weAllTeamSlice";
import {
  CONVERSATION_KEY,
  INBOX_KEY,
  UNREAD_KEY,
  useWeAllTeamUnread,
} from "@/hooks/useWeAllTeam";
import { toNumber } from "@/components/weAllTeam/weAllTeamUtils";

/**
 * Keeps the WeAll Team inbox live for the whole admin session (mounted once, in the sidebar).
 *
 * - Opens the admin socket while logged in and closes it on logout.
 * - Feeds the sidebar badge with the total unread count (initial read + live updates).
 * - Tells react-query which inbox / conversation data went stale, so any open WeAll Team page refreshes itself.
 *   Missed events are covered by refetching everything on every (re)connect.
 * Renders nothing.
 */
const WeAllTeamRealtime = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const isLoggedIn = useSelector((state) => state.user?.isLoggedIn);

  const { data: unreadResponse } = useWeAllTeamUnread(Boolean(isLoggedIn));
  const initialUnread = unreadResponse?.data?.total_unread;

  useEffect(() => {
    if (initialUnread !== undefined)
      dispatch(setWeAllTeamUnread(toNumber(initialUnread)));
  }, [initialUnread, dispatch]);

  useEffect(() => {
    if (!isLoggedIn) {
      disconnectAdminSocket();
      dispatch(setWeAllTeamUnread(0));
      return undefined;
    }

    const socket = connectAdminSocket();

    const refreshAll = () => {
      queryClient.invalidateQueries({ queryKey: INBOX_KEY });
      queryClient.invalidateQueries({ queryKey: CONVERSATION_KEY });
      queryClient.invalidateQueries({ queryKey: UNREAD_KEY });
    };

    const onUnread = (payload) => {
      const total = payload?.data?.total_unread;
      if (total !== undefined) dispatch(setWeAllTeamUnread(toNumber(total)));
    };
    const onConversationUpdated = (payload) => {
      onUnread(payload);
      queryClient.invalidateQueries({ queryKey: INBOX_KEY });
    };
    const onNewMessage = (payload) => {
      const conversationId = payload?.data?.conversation_id;
      queryClient.invalidateQueries({
        queryKey: conversationId
          ? [...CONVERSATION_KEY, conversationId]
          : CONVERSATION_KEY,
      });
    };
    // delivered / seen ticks of the team's own messages
    const onTick = (payload) => {
      const conversationId = payload?.data?.conversation_id;
      if (conversationId)
        queryClient.invalidateQueries({
          queryKey: [...CONVERSATION_KEY, conversationId],
        });
    };

    socket.on("connect", refreshAll);
    socket.on(WEALL_TEAM_EVENTS.UNREAD_COUNT, onUnread);
    socket.on(WEALL_TEAM_EVENTS.CONVERSATION_UPDATED, onConversationUpdated);
    socket.on(WEALL_TEAM_EVENTS.NEW_MESSAGE, onNewMessage);
    socket.on(WEALL_TEAM_EVENTS.MESSAGE_DELIVERED, onTick);
    socket.on(WEALL_TEAM_EVENTS.MESSAGE_SEEN, onTick);

    return () => {
      socket.off("connect", refreshAll);
      socket.off(WEALL_TEAM_EVENTS.UNREAD_COUNT, onUnread);
      socket.off(WEALL_TEAM_EVENTS.CONVERSATION_UPDATED, onConversationUpdated);
      socket.off(WEALL_TEAM_EVENTS.NEW_MESSAGE, onNewMessage);
      socket.off(WEALL_TEAM_EVENTS.MESSAGE_DELIVERED, onTick);
      socket.off(WEALL_TEAM_EVENTS.MESSAGE_SEEN, onTick);
    };
  }, [isLoggedIn, dispatch, queryClient]);

  return null;
};

export default WeAllTeamRealtime;
